import * as React from 'react';
import classNames from 'classnames';
import { injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import Tooltip from 'box-ui-elements/es/components/tooltip/Tooltip';
import Label from 'box-ui-elements/es/components/label/Label';
import DatePicker from 'box-ui-elements/es/components/date-picker/DatePicker';
import { dateFromZuluTime, dateToZuluTime } from '../../utils/time';
import messages from './messages';
import { DISPLAY_FORMAT } from '../../../constants';
// eslint-disable-next-line import/no-duplicates
import css from './buie-date-field.module.scss';
// eslint-disable-next-line import/no-duplicates
import './buie-date-field.module.scss';
import useId from '../../utils/useId';

type Props = {
  className?: string;
  description?: React.ReactNode;
  error?: React.ReactNode | string;
  infoText?: React.ReactNode | string;
  intl: IntlShape;
  isDisabled?: boolean;
  isInTwoColumn?: boolean;
  isRequired?: boolean;
  label: React.ReactNode;
  onChange: (...args: unknown[]) => unknown;
  value: string;
};

const BuieDateField = React.memo<Props>((props: Props) => {
  const {
    className = '',
    label,
    description,
    error,
    infoText,
    isRequired,
    isDisabled,
    onChange,
    value,
    isInTwoColumn,
    intl,
  } = props;

  const [isHovering, setIsHovering] = React.useState(false);

  const mouseProps = {
    onMouseEnter: () => setIsHovering(true),
    onMouseLeave: () => setIsHovering(false),
  } as const;

  const isTooltipShown = !!infoText && isHovering;
  // When user selects a date from date picker, set the date and turn off required error

  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  const onChangeDate = (date: any) => {
    const zuluAdjustedTime = date
      ? dateToZuluTime(date).toISOString()
      : undefined;
    onChange(zuluAdjustedTime);
  };

  const classes = classNames(className, 'date-field-container', {
    [css.withTooltip]: isTooltipShown,
    [css.withoutTooltip]: !isTooltipShown,
    [css.halfColumnWidth]: isInTwoColumn,
  });

  const placeholder = intl.formatMessage(messages.dateFieldPlaceholderMessage);

  const dateIsSet = (date: string) => {
    return !!date;
  };

  const hasError = !!error;

  const errorMessageID = useId('errorMessage');
  const descriptionID = useId('description');
  const ariaAttrs = {
    'aria-invalid': hasError,
    'aria-required': isRequired,
    'aria-errormessage': errorMessageID,
  } as const;

  const inputId = useId('date-input-id-');

  return (
    <div className={classes} data-testid="buie-date-field">
      <Label labelElProps={{ htmlFor: inputId }} text={label} />

      {!!description && (
        <div className="date-field-description" id={descriptionID}>
          {description}
        </div>
      )}

      <Tooltip
        className="field-tooltip"
        isShown={isTooltipShown}
        position="middle-right"
        text={infoText}
      >
        <div {...mouseProps}>
          <DatePicker
            className={error ? 'is-invalid' : ''}
            displayFormat={DISPLAY_FORMAT}
            error={error}
            errorTooltipPosition="top-left"
            hideOptionalLabel
            inputProps={{
              id: inputId,
              'aria-describedby': description ? descriptionID : undefined,
            }}
            isDisabled={isDisabled}
            isRequired={isRequired}
            onChange={onChangeDate}
            placeholder={placeholder}
            value={
              dateIsSet(value) ? dateFromZuluTime(new Date(value || 0)) : ''
            }
            {...ariaAttrs}
          />
        </div>
      </Tooltip>
    </div>
  );
});

type WrappedProps = Omit<Props, 'intl'>;
const Wrapped = injectIntl(BuieDateField);
export default Wrapped as React.ComponentType<WrappedProps>;
